import { z } from "zod";

const LEGACY_STORAGE_KEY = "user";
const STORAGE_KEY = "session";

const legacyStateSchema = z
  .object({
    authToken: z.string().min(1),
    user: z.object({
      id: z.string(),
      email: z.email(),
      firstName: z.string(),
      lastName: z.string(),
    }),
  })
  .transform(({ authToken, user }) => ({
    session: {
      status: "authenticated" as const,
      authToken,
      user: {
        ...user,
        fullName: [user.firstName, user.lastName]
          .filter((namePart) => namePart !== "")
          .join(" "),
      },
    },
  }));

function parseLegacyState(value: string) {
  try {
    const parsedValue: unknown = JSON.parse(value);
    const result = legacyStateSchema.safeParse(parsedValue);

    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Moves persisted authentication from the former user store to the Session
 * store before Pinia hydrates it. Remove this migration when direct upgrades
 * from releases that wrote the user key are no longer supported.
 */
export function migrateSessionStorage(storage: Storage) {
  try {
    if (storage.getItem(STORAGE_KEY) !== null) {
      storage.removeItem(LEGACY_STORAGE_KEY);
      return;
    }

    const legacyValue = storage.getItem(LEGACY_STORAGE_KEY);

    if (legacyValue === null) {
      return;
    }

    const migratedState = parseLegacyState(legacyValue);

    if (migratedState) {
      storage.setItem(STORAGE_KEY, JSON.stringify(migratedState));
    }

    storage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Persistence is optional. Storage failures must not block startup.
  }
}
